'use client';

import React, { useState, useEffect, useRef, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, Slider, getAccessibleFocusStyles } from '@videoblocks/design-system';
import ChevronDown from '@videoblocks/react-icons/ChevronDown';

export interface TextInputDropdownProps {
    value: number;
    minValue: number;
    maxValue: number;
    onChange: (value: number) => void;
    header: string;
    className?: string;
    disabled?: boolean;
}
export const TextInputDropdown = ({
    value,
    minValue,
    maxValue,
    onChange,
    header,
    className,
    disabled = false,
}: TextInputDropdownProps): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const formattedValue = `${(value / 100).toFixed(2)}x`;
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    return (
        <div className={twMerge('relative w-full md:w-auto', disabled && 'cursor-not-allowed')} ref={dropdownRef}>
            {/* Main Button */}
            <button
                onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
                aria-disabled={disabled}
                className={twMerge(
                    'flex w-full md:w-auto justify-center items-center gap-x-3 rounded-capsule border border-gray-500 px-3 py-3 bg-[#252c2d] cursor-pointer min-w-31 hover:bg-gray-700',
                    'disabled:opacity-50 disabled:pointer-events-none',
                    getAccessibleFocusStyles(),
                    className,
                )}
                disabled={disabled}
            >
                <Text fontWeight="regular">{formattedValue} Tempo</Text>
                <ChevronDown
                    className={`fill-current w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 rounded-lg shadow-xl z-10 border border-gray-700 w-[285px]">
                    <div className="px-4 py-3 border-b border-gray-700">
                        <h3 className="text-white font-semibold text-lg">{header}</h3>
                    </div>

                    <div className="py-3 px-4 mt-2 flex flex-col gap-2 items-end">
                        <Slider
                            value={[value]}
                            min={minValue}
                            max={maxValue}
                            disabled={disabled}
                            onValueChange={(newValue) => {
                                onChange(newValue[0]);
                            }}
                            trackProps={{
                                className: 'bg-white',
                            }}
                            rangeProps={{
                                className: 'bg-blue-600',
                            }}
                        />
                        <Text fontSize="sm" fontWeight="regular">
                            {formattedValue}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};
